import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { ArrowLeft, ArrowRight, Check, Users, FileText, Shield, Info } from 'lucide-react';
import { ref, uploadBytes } from 'firebase/storage';
import { storage } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useLifeStory } from '../contexts/LifeStoryContext';

export default function POAConsentScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { residentName, storyId, recordConsent } = useLifeStory();
  
  const [step, setStep] = useState(1);
  const [relationshipType, setRelationshipType] = useState(null);
  const [relationship, setRelationship] = useState('');
  const [checkboxes, setCheckboxes] = useState({
    authorized: false,
    recordStories: false,
    shareWithStaff: false,
    shareWithFamily: false,
    sharePublic: false,
    canDecline: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  const signatureRef = useRef(null);

  // Validation
  const isStep1Valid = relationshipType && relationship.trim();
  const isStep2Valid = 
    checkboxes.authorized && 
    checkboxes.recordStories && 
    checkboxes.shareWithStaff &&
    checkboxes.shareWithFamily &&
    checkboxes.canDecline;
  const isStep3Valid = signatureRef.current && !signatureRef.current.isEmpty();

  const canProceed = 
    (step === 1 && isStep1Valid) ||
    (step === 2 && isStep2Valid) ||
    (step === 3);

  const toggleCheckbox = (key) => {
    setCheckboxes(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleClearSignature = () => {
    signatureRef.current?.clear();
  };

  const handleSubmit = async () => {
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      setError('Please provide your signature');
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Get signature as data URL
      const signatureDataUrl = signatureRef.current.toDataURL('image/png');
      
      // Convert to blob and upload
      const response = await fetch(signatureDataUrl);
      const blob = await response.blob();
      
      const signaturePath = `life-stories/${storyId}/consent/poa_signature_${Date.now()}.png`;
      const storageRef = ref(storage, signaturePath);
      await uploadBytes(storageRef, blob);
      
      // Record consent
      await recordConsent({
        type: 'poa',
        verbal: false,
        poaAuthorization: {
          authorizedBy: {
            uid: user.uid,
            name: user.displayName,
            email: user.email,
            relationship: relationship
          },
          relationshipType: relationshipType,
          signatureUrl: signaturePath,
          publicSharingAllowed: checkboxes.sharePublic
        },
        publicSharingAllowed: checkboxes.sharePublic
      });
      
      // Navigate to recording
      navigate(`/story/${storyId}`, { replace: true });
      
    } catch (err) {
      console.error('POA consent error:', err);
      setError('Failed to submit authorization. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    if (step < 3) {
      setStep(step + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col safe-area-top safe-area-bottom">
      {/* Header */}
      <header className="bg-white border-b px-4 py-3">
        <div className="flex items-center justify-between">
          <button
            onClick={handleBack}
            className="p-2 -ml-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <h1 className="font-semibold text-gray-900">POA Authorization</h1>
          <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
            Step {step} of 3
          </span>
        </div>
        
        {/* Progress bar */}
        <div className="mt-3 h-1 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-quiltt-500 transition-all duration-300"
            style={{ width: `${(step / 3) * 100}%` }}
          />
        </div>
      </header>
      
      {/* Content */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-lg mx-auto p-4 md:p-6">
          {step === 1 && (
            <Step1Relationship
              residentName={residentName}
              relationshipType={relationshipType}
              setRelationshipType={setRelationshipType}
              relationship={relationship}
              setRelationship={setRelationship}
            />
          )}
          
          {step === 2 && (
            <Step2Authorization
              residentName={residentName}
              checkboxes={checkboxes}
              toggleCheckbox={toggleCheckbox}
            />
          )}
          
          {step === 3 && (
            <Step3Signature
              residentName={residentName}
              relationship={relationship}
              signatureRef={signatureRef}
              onClear={handleClearSignature}
            />
          )}
        </div>
      </div>
      
      {/* Footer */}
      <div className="bg-white border-t p-4">
        <div className="max-w-lg mx-auto">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}
          
          <button
            onClick={handleNext}
            disabled={!canProceed || isSubmitting}
            className="w-full flex items-center justify-center gap-2 bg-quiltt-500 hover:bg-quiltt-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-colors"
          >
            {isSubmitting ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Submitting...
              </>
            ) : step === 3 ? (
              <>
                Submit Authorization
                <Check className="w-5 h-5" />
              </>
            ) : (
              <>
                Continue
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}


// ============================================
// Step Components
// ============================================

function Step1Relationship({ 
  residentName, 
  relationshipType, 
  setRelationshipType,
  relationship,
  setRelationship 
}) {
  const options = [
    { id: 'family', label: 'Family Member', icon: Users },
    { id: 'poa', label: 'Power of Attorney', icon: FileText },
    { id: 'legal_guardian', label: 'Legal Guardian', icon: Shield },
  ];
  
  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Your Relationship</h2>
      <p className="text-gray-600 mb-6">
        Please tell us about your relationship to {residentName}.
      </p>
      
      <label className="block text-sm font-medium text-gray-700 mb-3">
        I am {residentName}'s:
      </label>
      
      <div className="grid grid-cols-3 gap-3 mb-6">
        {options.map(option => (
          <button
            key={option.id}
            onClick={() => setRelationshipType(option.id)}
            className={`
              p-4 rounded-xl border-2 flex flex-col items-center text-center transition-all
              ${relationshipType === option.id 
                ? 'border-quiltt-500 bg-quiltt-50' 
                : 'border-gray-200 hover:border-gray-300'
              }
            `}
          >
            <option.icon className={`w-7 h-7 mb-2 ${
              relationshipType === option.id ? 'text-quiltt-600' : 'text-gray-400'
            }`} />
            <span className={`text-xs font-medium ${
              relationshipType === option.id ? 'text-quiltt-700' : 'text-gray-600'
            }`}>
              {option.label}
            </span>
          </button>
        ))}
      </div>
      
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Specific relationship (e.g., "Daughter", "Son-in-law"):
      </label>
      <input
        type="text"
        value={relationship}
        onChange={(e) => setRelationship(e.target.value)}
        placeholder="Enter your relationship"
        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-quiltt-500 focus:border-transparent outline-none"
      />
    </div>
  );
}

function Step2Authorization({ residentName, checkboxes, toggleCheckbox }) {
  const items = [
    {
      key: 'authorized',
      label: `I am authorized to make decisions on behalf of ${residentName}.`,
      required: true
    },
    {
      key: 'recordStories',
      label: `I authorize the recording of ${residentName}'s life stories.`,
      required: true
    },
    {
      key: 'shareWithStaff',
      label: 'I understand these stories may be shared with care staff to provide better, more personalized care.',
      required: true
    },
    {
      key: 'shareWithFamily',
      label: 'I understand these stories will be accessible to family members.',
      required: true
    },
    {
      key: 'sharePublic',
      label: 'I authorize sharing appropriate stories on community displays (e.g., birthday announcements with fun facts).',
      required: false
    },
    {
      key: 'canDecline',
      label: `I acknowledge that ${residentName} may decline to answer any question at any time.`,
      required: true
    },
  ];
  
  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Authorization</h2>
      <p className="text-gray-600 mb-6">
        Please review and confirm the following:
      </p>
      
      <div className="space-y-3">
        {items.map(item => (
          <button
            key={item.key}
            onClick={() => toggleCheckbox(item.key)}
            className="w-full flex items-start gap-3 p-4 bg-white rounded-lg border border-gray-200 hover:border-gray-300 text-left transition-colors"
          >
            <div className={`
              w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors
              ${checkboxes[item.key] 
                ? 'bg-quiltt-500 border-quiltt-500' 
                : 'border-gray-300'
              }
            `}>
              {checkboxes[item.key] && <Check className="w-3 h-3 text-white" />}
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-800">{item.label}</p>
              <span className={`text-xs mt-1 ${
                item.required ? 'text-red-500' : 'text-gray-400'
              }`}>
                {item.required ? 'Required' : 'Optional'}
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function Step3Signature({ residentName, relationship, signatureRef, onClear }) {
  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Digital Signature</h2>
      <p className="text-gray-600 mb-6">
        Please sign below to confirm your authorization.
      </p>
      
      <div className="bg-white rounded-lg border-2 border-gray-200 overflow-hidden mb-3">
        <SignatureCanvas
          ref={signatureRef}
          canvasProps={{
            className: 'signature-canvas w-full h-48'
          }}
          backgroundColor="white"
        />
      </div>
      
      <button
        onClick={onClear}
        className="text-sm text-gray-500 hover:text-gray-700 underline mb-6"
      >
        Clear signature
      </button>
      
      <div className="flex gap-3 p-4 bg-gray-50 rounded-lg">
        <Info className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-gray-600">
          By signing, I confirm that I am {relationship || 'a representative'} of {residentName} and 
          am authorized to provide this consent on their behalf.
        </p>
      </div>
    </div>
  );
}
